/* Tiny fetch wrapper. Adds JWT, parses JSON, throws on non-2xx.
   Will be replaced by an OpenAPI-generated client in a later sprint. */

import { useAuthStore } from "@/lib/auth/store";

const API_BASE = "/api/v1";

export class ApiError extends Error {
  constructor(
    public status: number,
    public payload: unknown,
    message: string,
    public retryAfter?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Thrown when the server returns 429 Too Many Requests. */
export class RateLimitError extends ApiError {
  public readonly scope: string;

  constructor(retryAfter: number, scope: string, payload: unknown) {
    super(429, payload, `Rate limited. Retry after ${retryAfter}s.`, retryAfter);
    this.name = "RateLimitError";
    this.scope = scope;
  }
}

async function refreshToken(): Promise<string | null> {
  const { refresh } = useAuthStore.getState();
  if (!refresh) return null;
  const res = await fetch(`${API_BASE}/auth/refresh/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh }),
  });
  if (!res.ok) {
    useAuthStore.getState().clear();
    return null;
  }
  const data = (await res.json()) as { access: string; refresh?: string };
  useAuthStore.getState().setTokens({
    access: data.access,
    refresh: data.refresh ?? refresh,
  });
  return data.access;
}

export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit = {},
  retried = false,
): Promise<T> {
  const { access } = useAuthStore.getState();
  const headers = new Headers(init.headers);
  // Never force Content-Type on FormData — the browser must set the
  // multipart boundary automatically. Only add JSON for non-FormData bodies.
  if (!headers.has("Content-Type") && init.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  if (access) {
    headers.set("Authorization", `Bearer ${access}`);
  }
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });

  if (res.status === 401 && !retried) {
    const newAccess = await refreshToken();
    if (newAccess) return apiFetch<T>(path, init, true);
  }

  const text = await res.text();
  const data = text ? safeJson(text) : null;

  if (res.status === 429) {
    const body = data as Record<string, unknown> | null;
    const retryAfter =
      typeof body?.retry_after === "number"
        ? body.retry_after
        : parseInt(res.headers.get("Retry-After") ?? "60", 10);
    const scope = typeof body?.scope === "string" ? body.scope : "unknown";
    throw new RateLimitError(retryAfter, scope, data);
  }

  if (!res.ok) {
    console.error(`[API ${res.status}] ${init.method ?? "GET"} ${path}`, data);
    // For 5xx debug responses that include a Python traceback, print it readably.
    if (res.status >= 500 && data && typeof data === "object" && "trace" in data) {
      console.error("=== SERVER TRACEBACK ===\n" + (data as Record<string, unknown>).trace);
    }
    const detail =
      data && typeof data === "object" && "detail" in data
        ? String((data as Record<string, unknown>).detail)
        : data && typeof data === "object" && "error" in data
          ? String((data as Record<string, unknown>).error)
          : `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, data, detail);
  }
  return data as T;
}

export async function apiFetchBlob(
  path: string,
  init: RequestInit = {},
  retried = false,
): Promise<Blob> {
  const { access } = useAuthStore.getState();
  const headers = new Headers(init.headers);
  if (access) {
    headers.set("Authorization", `Bearer ${access}`);
  }
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });

  if (res.status === 401 && !retried) {
    const newAccess = await refreshToken();
    if (newAccess) return apiFetchBlob(path, init, true);
  }

  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    const errBody = errText ? safeJson(errText) : null;
    const detail =
      errBody && typeof errBody === "object" && "detail" in errBody
        ? String((errBody as Record<string, unknown>).detail)
        : `${res.status} ${res.statusText}`;
    throw new ApiError(res.status, errBody, detail);
  }
  return res.blob();
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
